/**
 * Workspace API - read the current workspace state from compute-api.
 *
 * Backed by GET /api/v1/workspace, which derives the workspace from the first
 * NamespaceConfig in the deployed namespace.
 */
import apiClient from './client';

/** The backend serving the workspace's catalogs. For labelling only; gate on CatalogFeatures. */
export type CatalogBackend = 'metadata' | 'storage-schema';

/**
 * Catalog capabilities that depend on the backend. The console gates each backend-specific
 * control on one of these, never on CatalogBackend, so a backend gaining a capability needs
 * no console change and retiring a backend is a search for each feature name.
 */
export interface CatalogFeatures {
  /** One on-demand refresh of every catalog's listings, from the SQL editor. */
  forceRefresh: boolean;
  /** Refreshing one catalog at a time: the refresh action, schedules and refresh activity. */
  perCatalogRefresh: boolean;
  /** Access policies the backend enforces. */
  governance: boolean;
}

export interface WorkspaceInfo {
  /** NamespaceConfig CR name */
  name: string;
  /** Kubernetes namespace */
  namespace: string;
  /**
   * The DNS-safe workspace slug (NamespaceConfig.spec.workspace) — the internal
   * identifier. May be empty when unset; callers should fall back to `name`.
   */
  workspace?: string;
  /**
   * Human-readable workspace name (NamespaceConfig.spec.displayName), shown to
   * users. Falls back to the slug on pre-migration workspaces (compute-api
   * returns the slug when displayName is unset).
   */
  displayName?: string;
  suspended: boolean;
  phase: string;
  suspensionState?: Record<string, unknown>;
  /** Absent when the namespace has no MetadataServices. */
  catalogBackend?: CatalogBackend;
  catalogFeatures?: CatalogFeatures;
}

const API_BASE = '/api/v1/workspace';

export const workspaceApi = {
  get: (): Promise<WorkspaceInfo> => apiClient.get(API_BASE),
};

export default workspaceApi;
